import React, { useMemo, useState } from "react";
import { Grid, Box, Link, Typography, IconButton } from "@mui/material";
import CustomTextField from "../../../../components/CustomTextField/CustomTextField";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { ConfirmModal } from "components";
import { ContentsService } from "services/ContentsService";
import { Timestamp } from "firebase/firestore";
import { set } from "react-hook-form";
import UploadedFiles from "modules/CreateHandout/components/CreateHandoutData/UploadedFiles";

const ContentStructureField = ({ content, setContent }: any) => {
  const [isImageShow, setImageShow] = useState(false);

  const [formValues, setFormValues] = useState({
    contentStracture: "",
  });
  const [sections, setSections] = useState<any[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string | null>(null);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);
  const [fileToDelete, setFileToDelete] = useState<number | null>(null);

  const [uploadedFiles, setUploadedFiles] = useState<{ name: string; type: string; url: string }[]>([]);


  const handleFileSelect = async (file: File) => {
    setLoading(true);
    try {
      const fileType = file.type.startsWith("image/") ? "image" : file.type === "application/pdf" ? "pdf" : null;
      if (!fileType) {
        alert("Only image and PDF files are allowed.");
        setLoading(false);
        return;
      }

      // Upload file and get the URL
      const downloadURL = await ContentsService.uploadHandoutPicture("", file);

      setUploadedFiles([{ name: file.name, type: fileType, url: downloadURL }]);
      setFileName(file.name);
      setFileType(fileType);

      setContent((prevData: any) => {
        const updatedContentStructure = [...(prevData.contentStructure || [])];

        // ✅ Find existing section by title
        const existingSectionIndex = updatedContentStructure.findIndex(
          (section: any) => section.title === formValues.contentStracture
        );

        if (existingSectionIndex !== -1) {
          // ✅ Ensure `files` array exists before pushing
          if (!Array.isArray(updatedContentStructure[existingSectionIndex].files)) {
            updatedContentStructure[existingSectionIndex].files = [];
          }

          updatedContentStructure[existingSectionIndex].files.push({
            fileUrl: downloadURL,
            name: file.name,
            type: fileType,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          });
        } else {
          // ✅ Create new section with initialized `files` array
          updatedContentStructure.push({
            title: formValues.contentStracture,
            files: [
              {
                fileUrl: downloadURL,
                name: file.name,
                type: fileType,
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
              },
            ],
          });
        }

        return { ...prevData, contentStructure: updatedContentStructure };
      });

      // setShowIcons((prevState: any) => ({
      //   ...prevState,
      //   contentstructure: true,
      // }));
    } catch (error) {
      console.error("Error uploading file:", error);
    }

    setLoading(false);
    setImageShow(false);
    setFormValues({ contentStracture: "" });
  };



  const handleSameSectionUpload = (title: string) => {
    setImageShow(true)
    if (content.contentStructure?.length) {
      setFormValues({ contentStracture: title });
    } else {
      setFormValues({ ...formValues });
    }

    // Reset file-related states
    setUploadedFiles([]);
    setFileName(null);
    setFileType(null);
  };
  // Handle section title input change
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormValues({ contentStracture: event.target.value }); // ✅ Correct
  };


  const handleDelete = () => {
    if (fileToDelete !== null) {

      // Remove the deleted file from the content structure
      const updatedContentStructure = content?.contentStructure?.filter(
        (section: any, sIndex: number) => sIndex !== fileToDelete
      );
      setContent({ ...content, contentStructure: updatedContentStructure })

      // Close modal and reset selected delete indexes
      setConfirmModalOpen(false);
      setFileToDelete(null);

      // / Hide the icon if no sections are left
      // if (updatedContentStructure.length === 0) {
      //   setShowIcons((prevState: any) => ({
      //     ...prevState,
      //     contentstructure: false,
      //   }));
      // }
    }
  };


  // Trigger the modal before deleting the file
  const handleSelectDeleteFile = (fileIndex: number) => {

    setFileToDelete(fileIndex);
    setConfirmModalOpen(true);
  };


  // Function to add a new section
  const addNewSection = () => {
    if (formValues.contentStracture.trim() !== "") {
      setContent((prevData: any) => ({
        ...prevData,
        contentStructure: [
          ...prevData.contentStructure,
          { title: formValues.contentStracture, ...uploadedFiles },
        ],
      }))

      // Show the icon when a section is added
      // setShowIcons((prevState: any) => ({
      //   ...prevState,
      //   contentstructure: true,
      // }));
      // Clear the input and uploaded files after adding the section
      setFormValues({ contentStracture: "" });
      setUploadedFiles([]);
      setFileName(null);
      setFileType(null);
      setImageShow(false);
    }
  };



  // Function to delete a section
  const deleteSection = (title: string) => {
    const updatedContentStructure = content.contentStructure.filter(
      (section: any) => section.title !== title
    )
    setContent({ ...content, contentStructure: updatedContentStructure })
    // Hide the icon if no sections are left
    // if (updatedContentStructure.length === 0) {
    //   setShowIcons((prevState: any) => ({
    //     ...prevState,
    //     contentstructure: false,
    //   }));
    // }
  };

  function groupByTitle(dataArray: any) {
    if (!Array.isArray(dataArray)) return {}; // Ensure input is an array

    return dataArray.reduce((groupedData: any, currentItem: any) => {
      const { title, files } = currentItem;

      if (!groupedData[title]) {
        groupedData[title] = [];
      }

      // ✅ Ensure `files` is always an array before spreading
      if (Array.isArray(files)) {
        groupedData[title] = [...groupedData[title], ...files];
      } else {
        console.warn(`Skipping invalid files for section "${title}"`, files);
      }

      return groupedData;
    }, {});
  }


  const newData = useMemo(() => {
    return groupByTitle(content.contentStructure || []); // Provide a fallback to an empty array
  }, [content.contentStructure]);




  return (

    <Grid
      display="flex"
      gap={5}
      flexDirection="column"
      p={{ xs: 2, md: 5 }}
      mt={{ xs: 3, md: 0 }}
    >
      <Grid item xs={12} sm={6} display="flex" gap={1} flexDirection="column">
        <label htmlFor="contentStracture" style={styleSheet.labelText}>
          Section Title
        </label>
        <Grid display="flex" alignItems="center" flex={1}>
          <Box
            sx={{ flexGrow: 1, display: "flex", gap: 1, alignItems: "center" }}
          >
            <CustomTextField
              name="contentStracture"
              sx={{ width: { xs: "233px", md: "100%" }, maxWidth: "600px" }}
              value={formValues.contentStracture}
              onChange={handleChange}
            />
            <Link
              color="#000000"
              sx={{
                color: "#000000",
                fontSize: "17px",
                fontWeight: 400,
                cursor: "pointer",
              }}
              onClick={addNewSection} // Add section on click
            >
              Add a New Section
            </Link>
          </Box>
        </Grid>

        {/* Optional file uploader logic */}
        <Link
          onClick={() => setImageShow(true)}
          sx={{
            color: "#989898",
            fontSize: "17px",
            fontWeight: 400,
            m: 1,
            cursor: "pointer",
          }}
        >
          Add a Section File
        </Link>

        {/* Render the uploaded files component */}
        {isImageShow && (
          <UploadedFiles
            fileName={fileName}
            fileType={fileType}
            onFileSelect={handleFileSelect}
            isLoading={isLoading}
            onDelete={() => { }}
            onConfirm={() => { }}
            isConfirmModalOpen={isConfirmModalOpen}
            setConfirmModalOpen={setConfirmModalOpen}
            subTitle="Select Images or PDFs"
            modalTitle="Are you sure you want to delete the section?"
            modalSubtitle="Figma ipsum component variant main layer. Asset opacity rotate plugin line. Project pen frame share."
          />
        )}
      </Grid>

      {/* Render the dynamically added sections */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {Object.entries(newData).map(([title, files], index) => (
          <>
            <Box
              key={index}
              sx={{
                width: "100%",
                maxWidth: "570px",
                bgcolor: "#E8E8E8",
                border: "1px solid #BFBFBF",
                borderRadius: "9px",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography>{title} </Typography>
                <IconButton onClick={() => deleteSection(title)}>
                  <ClearOutlinedIcon sx={{ color: "#BFBFBF", cursor: "pointer" }} />
                </IconButton>
              </Box>

              {/* Render the uploaded files for the current section */}
              {
                //@ts-ignore
                files?.map((file: any, fileIndex: number) => {

                  if (!file?.fileUrl) return
                  return (
                    <Box
                      key={fileIndex}
                      pl="8px"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        width: { xs: "321px", md: "500px" },
                        border: "0.78px dashed #393939",
                        borderRadius: "8px",
                        height: "51px",
                      }}
                    >
                      <Box display="flex" gap="3px" alignItems="center">
                        <InsertDriveFileIcon color="disabled" />
                        <Typography fontSize={18} fontWeight={700} color="#595959">
                          {file.name}({file.type} {index + fileIndex})
                        </Typography>
                      </Box>
                      <IconButton
                        onClick={() => handleSelectDeleteFile(file.index)}
                        sx={{ marginRight: 1, color: "#E10A0A" }}
                      >
                        <img src="/assets/icons/iconDelete.png" alt="Delete Icon" />
                      </IconButton>
                    </Box>
                  )
                })}


            </Box>
            {/* Add more files to the same section */}
            <Link
              onClick={() => handleSameSectionUpload(title)}
              sx={{
                color: "#000000",
                fontSize: "17px",
                fontWeight: 400,
                m: 1,
                cursor: "pointer",
              }}
            >
              Add a Section File
            </Link>
          </>
        ))}


      </Box>

      {/* Confirm Delete Modal */}
      <ConfirmModal
        title="Confirm Delete"
        subtitle="Are you sure you want to delete this file?"
        cancelText="Cancel"
        confirmText="Delete"
        titleColor="#020303"
        isLoading={false}
        onCancel={() => setConfirmModalOpen(false)}
        onConfirm={handleDelete}
        isConfirmModal={isConfirmModalOpen}
        warning="Warning"
      />
    </Grid>
  );
};

export default ContentStructureField;

const styleSheet = {
  labelText: {
    fontWeight: 400,
    fontSize: "17px",
    color: "#000000",
  },
};